import { ChevronDown, type LucideIcon } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';

interface CollapsibleSectionHeaderProps {
  icon?: LucideIcon;
  iconNode?: ReactNode;
  label: string;
  open: boolean;
  onToggle: () => void;
  extra?: ReactNode;
  badge?: string;
  statusBadge?: string;
  alwaysOpen?: boolean;
}

export function CollapsibleSectionHeader({
  icon: Icon,
  iconNode,
  label,
  open,
  onToggle,
  extra,
  badge,
  statusBadge,
  alwaysOpen = false,
}: CollapsibleSectionHeaderProps) {
  const renderedIcon =
    iconNode ?? (Icon ? <Icon size={13} className="text-muted-foreground" /> : null);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (alwaysOpen) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  }

  return (
    <div
      role={alwaysOpen ? undefined : 'button'}
      tabIndex={alwaysOpen ? undefined : 0}
      onClick={alwaysOpen ? undefined : onToggle}
      onKeyDown={handleKeyDown}
      className={`mb-3 flex w-full select-none items-center justify-between${
        alwaysOpen ? '' : ' cursor-pointer'
      }`}
    >
      <div className="flex items-center gap-2">
        {renderedIcon && (
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background">
            {renderedIcon}
          </div>
        )}
        <span className="text-sm font-semibold">{label}</span>
        {badge === 'required' && (
          <span className="rounded-full bg-muted px-1.5 py-px text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Required
          </span>
        )}
        {statusBadge && (
          <span className="rounded-full bg-success/10 px-1.5 py-px text-[10px] font-semibold text-success">
            {statusBadge}
          </span>
        )}
        {badge === 'optional' && (
          <span className="text-[11px] text-muted-foreground">— optional</span>
        )}
        {badge && badge !== 'required' && badge !== 'optional' && (
          <span className="rounded-full bg-muted px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {badge}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {extra && <div onClick={(event) => event.stopPropagation()}>{extra}</div>}
        {!alwaysOpen && (
          <ChevronDown
            size={16}
            className={`text-muted-foreground transition-transform duration-150${open ? '' : ' -rotate-90'}`}
          />
        )}
      </div>
    </div>
  );
}
